import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MailCheck, MailX } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { LoadingState } from '@/components/ui/states';
import { useToast } from '@/components/ui/toast';
import { teamsApi } from '@/lib/api/endpoints';
import { describeFailure } from '@/lib/i18n/errors';
import { useT } from '@/lib/i18n';

/**
 * An invitation link opened in a browser that is already signed in.
 *
 * This used to redirect silently to /teams, which is indistinguishable from
 * the link being broken — the person clicks, something flashes, and they are
 * no more in the team than before. Following a link a colleague sent you while
 * already logged in is the *common* case, not an edge one.
 *
 * The address is stated before the button, because the invitation is bound to
 * it: being signed in as somebody else is not a way around that, and finding
 * out after clicking is worse than reading it first.
 */
export function AcceptInviteRoute({ email }: { email: string }) {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { token = '' } = useParams();

  const { data, isPending, error } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => teamsApi.previewInvitation(token),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () => teamsApi.acceptInvitation(token),
    onSuccess: (invitation) => {
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
      toast.show(
        invitation.team_name
          ? t('invite.joined', { team: invitation.team_name })
          : t('invite.joinedNoTeam'),
      );
      void navigate('/teams', { replace: true });
    },
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });

  if (isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingState />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Centre>
        <MailX className="h-8 w-8 text-content-subtle" aria-hidden />
        <h1 className="text-lg font-semibold text-content">{t('invite.deadTitle')}</h1>
        <p className="max-w-sm text-sm text-content-muted">{t('invite.deadHint')}</p>
        <Button variant="secondary" size="sm" onClick={() => void navigate('/today')}>
          {t('invite.backToApp')}
        </Button>
      </Centre>
    );
  }

  // The invitation names an address. Say so before the button rather than
  // after a refusal the person cannot act on.
  const mismatched = data.email.toLowerCase() !== email.toLowerCase();

  return (
    <Centre>
      <MailCheck className="h-8 w-8 text-accent" aria-hidden />
      <div>
        <h1 className="text-lg font-semibold text-content">
          {data.team_name
            ? t('invite.welcomeTeam', { team: data.team_name })
            : t('invite.welcome')}
        </h1>
        <p className="mt-1.5 max-w-sm text-sm text-content-muted">
          {data.invited_by_name
            ? t('invite.invitedBy', { name: data.invited_by_name })
            : t('invite.invitedByUnknown')}
          {data.role === 'MANAGER' ? ` ${t('invite.asManagerNote')}` : ''}
        </p>
      </div>

      {mismatched ? (
        <p className="max-w-sm rounded-lg bg-amber/10 px-3 py-2 text-sm text-amber">
          {t('invite.wrongAccount', { invited: data.email, current: email })}
        </p>
      ) : (
        <Button onClick={() => accept.mutate()} disabled={accept.isPending}>
          {t('invite.accept')}
        </Button>
      )}

      <button
        type="button"
        onClick={() => void navigate('/today')}
        className="text-xs text-content-muted hover:text-content"
      >
        {t('invite.backToApp')}
      </button>
    </Centre>
  );
}

function Centre({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-5 px-6 text-center">
      {children}
    </div>
  );
}
